/**
 * HxTabbedView Component
 *
 * A layout component that displays content organized in tabs with configurable orientation. Consists of a tabs-list,
 * divider, and content panel area.
 *
 * @example
 *   ```html
 *   <hx-tabbed-view
 *     [tabs]="vaccineTabsData"
 *     orientation="vertical"
 *     tabsRatio="30%"
 *     (tabChange)="onTabChange($event)">
 *
 *     <ng-template hxTabPanel="pcv">
 *       <div>Pneumococcal Conjugate Vaccine content</div>
 *     </ng-template>
 *
 *     <ng-template hxTabPanel="influenza">
 *       <div>Influenza Vaccine content</div>
 *     </ng-template>
 *   </hx-tabbed-view>
 *   ```;
 *
 * @package @complexcare/harmony
 * @feature 043-harmony-tabbed-view
 */

import { NgTemplateOutlet } from '@angular/common';
import {
  AfterContentInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  contentChildren,
  effect,
  ElementRef,
  forwardRef,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  output,
  runInInjectionContext,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormGroup,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { focusControl, getActiveElement, getTabStops, indexOfTabStop, isPlainTab } from '../../a11y';
import { HxDivider } from '../divider/divider';
import { HxIcon } from '../icon/icon';
import { HxTabPanelDirective } from './tab-panel.directive';
import type { HxTab, HxTabChangeEvent, TabbedViewOrientation, TabbedViewTabsPosition } from './types';

@Component({
  selector: 'hx-tabbed-view',
  standalone: true,
  imports: [NgTemplateOutlet, HxDivider, HxIcon],
  templateUrl: './tabbed-view.html',
  styleUrls: ['./tabbed-view.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class]': 'hostClasses()',
    '[style.--tabs-ratio]': 'tabsRatio()',
    '[attr.role]': '"region"',
    '(keydown)': 'handleHostKeydown($event)',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HxTabbedView),
      multi: true,
    },
  ],
})
export class HxTabbedView implements ControlValueAccessor, OnInit, AfterContentInit, OnDestroy {
  // Content projection queries
  readonly tabPanels = contentChildren(HxTabPanelDirective);

  // --- INPUTS ---

  /**
   * Array of tab configurations. Each tab must have a unique key.
   *
   * @required
   */
  readonly tabs = input.required<HxTab[]>();

  /**
   * Layout orientation: vertical (tabs on left) or horizontal (tabs on top).
   *
   * @default 'vertical'
   */
  readonly orientation = input<TabbedViewOrientation>('vertical');

  /**
   * Position of the tabs-list. For vertical orientation: 'start' = left, 'end' = right. For horizontal orientation:
   * 'start' = top, 'end' = bottom.
   *
   * @default 'start'
   */
  readonly tabsPosition = input<TabbedViewTabsPosition>('start');

  /**
   * Space allocation for the tabs-list area as a CSS value (%, px, etc.). Applies to width in vertical orientation,
   * height in horizontal orientation.
   *
   * @default '25%'
   */
  readonly tabsRatio = input<string>('25%');

  /** Key of the tab that should be selected by default on initial load. If not provided, the first tab is selected. */
  readonly defaultTab = input<string | undefined>(undefined);

  /** Programmatically control which tab is selected. When provided, component operates in controlled mode. */
  readonly selectedTab = input<string | undefined>(undefined);

  /**
   * Whether to display the divider between tabs-list and content panel.
   *
   * @default true
   */
  readonly showDivider = input(true, { transform: booleanAttribute });

  /**
   * Message to display when tabs are incomplete and submission is blocked. Displayed in the panel area when there are
   * invalid tabs.
   *
   * @default 'Please complete all required tabs before continuing.'
   */
  readonly incompleteTabsMessage = input<string>('Please complete all required tabs before continuing.');

  /**
   * ARIA label for the tabbed view region.
   *
   * @default 'Tabbed view'
   */
  readonly ariaLabel = input<string>('Tabbed view');

  // --- OUTPUTS ---

  /** Emitted when the active tab changes. */
  readonly tabChange = output<HxTabChangeEvent>();

  /** Emitted when a tab icon is clicked. */
  readonly tabIconClick = output<{ key: string; tab: HxTab }>();

  /** Emitted when validation status of a tab changes */
  readonly tabValidationChange = output<{
    tabKey: string;
    isValid: boolean;
    errors: ValidationErrors | null;
  }>();

  /** Emitted when overall validation status changes */
  readonly validationChange = output<{
    isValid: boolean;
    invalidTabs: string[];
  }>();

  // --- INTERNAL STATE ---

  /**
   * Internal signal tracking the currently active tab key.
   *
   * @internal
   */
  protected readonly activeTabKey = signal<string | null>(null);

  /**
   * Unique ID for ARIA relationships.
   *
   * @internal
   */
  protected readonly uniqueId = `hx-tabbed-view-${Math.random().toString(36).substring(2, 9)}`;

  // --- FORM MANAGEMENT ---

  /** FormGroup for each tab, keyed by tab.key */
  protected readonly tabFormGroups = new Map<string, FormGroup>();

  /** Query all NgControl instances in projected content */
  protected childControls = contentChildren(NgControl, { descendants: true });

  /** Track subscriptions for cleanup */
  protected subscriptions: Subscription[] = [];

  /** Signal to trigger recomputation of tab validation status */
  protected readonly validationStatusTrigger = signal<number>(0);

  /** Host element reference for DOM queries */
  private readonly hostElementRef = inject(ElementRef<HTMLElement>);

  /** ChangeDetectorRef for synchronous view updates during keyboard transitions */
  private readonly cdr = inject(ChangeDetectorRef);

  /** Injector for creating effects outside constructor */
  private readonly injector = inject(Injector);

  /** ControlValueAccessor callbacks */
  protected onChange: (value: any) => void = () => {};
  protected onTouched: () => void = () => {};

  /** Current aggregated value (tab-keyed structure) */
  protected currentValue: Record<string, any> = {};

  // --- COMPUTED SIGNALS ---

  /**
   * Enhanced tabs array with computed validation status per tab. Since all tabs are now kept in DOM, we can directly
   * check FormGroup validity. Tracks validationStatusTrigger to react to FormGroup status changes.
   *
   * @internal
   */
  protected readonly enhancedTabs = computed<HxTab[]>(() => {
    const baseTabs = this.tabs();
    this.validationStatusTrigger();

    if (!baseTabs) return [];

    return baseTabs.map((tab) => ({
      ...tab,
      status: tab.status ?? this.computeTabStatus(tab.key),
    }));
  });

  /**
   * Computed CSS class list for the host element.
   *
   * @internal
   */
  protected readonly hostClasses = computed(() => {
    const classes: string[] = ['hx-tabbed-view', `hx-tabbed-view--${this.orientation()}`];

    if (this.tabsPosition() === 'end') {
      classes.push('hx-tabbed-view--tabs-end');
    }

    return classes.join(' ');
  });

  /**
   * Computed signal returning the currently active tab object.
   *
   * @internal
   */
  protected readonly activeTab = computed(() => {
    const key = this.activeTabKey();
    if (!key) {
      return null;
    }
    return this.enhancedTabs().find((tab) => tab.key === key) ?? null;
  });

  /** Handle tab icon click without selecting/changing tab. */
  protected handleTabIconClick(event: MouseEvent, tab: HxTab): void {
    event.stopPropagation();
    event.preventDefault();
    this.tabIconClick.emit({ key: tab.key, tab });
  }

  /**
   * Whether the divider should be oriented vertically. Vertical orientation uses vertical divider, horizontal uses
   * horizontal divider.
   *
   * @internal
   */
  protected readonly isDividerVertical = computed(() => this.orientation() === 'vertical');

  /**
   * Computed signal to check if any tab has invalid status.
   *
   * @internal
   */
  protected readonly hasInvalidTabs = computed(() => {
    const tabs = this.enhancedTabs();
    return tabs.some((tab) => tab.status === 'invalid');
  });

  /**
   * Computed signal to determine if the incomplete message banner should be shown. Shows when there are invalid tabs
   * (validation errors).
   *
   * @internal
   */
  protected readonly shouldShowIncompleteBanner = computed(() => {
    const tabs = this.enhancedTabs();
    return tabs.some((tab) => tab.status === 'invalid' && this.hasNonReferralValidationError(tab.key));
  });

  private hasNonReferralValidationError(tabKey: string): boolean {
    const formGroup = this.tabFormGroups.get(tabKey);
    if (!formGroup) return false;

    return Object.values(formGroup.controls).some((control) => {
      const errors = control.errors;
      return control.invalid && !!errors && Object.keys(errors).some((errorKey) => errorKey !== 'notesRequired');
    });
  }

  constructor() {
    // Effect to initialize the active tab on first render
    effect(() => {
      const tabs = this.tabs() ?? [];
      const selectedKey = this.selectedTab();
      const defaultKey = this.defaultTab();
      const currentKey = this.activeTabKey();

      if (tabs.length === 0) return;

      // If controlled mode (selectedTab provided), sync with it
      if (typeof selectedKey === 'string') {
        if (currentKey !== selectedKey) {
          this.activeTabKey.set(selectedKey as string);
        }
        return;
      }

      // Initialize active tab if not set
      if (currentKey === null && tabs.length > 0) {
        // Use defaultTab if provided and valid, otherwise first tab
        const firstTab = tabs[0];
        if (!firstTab) {
          return;
        }
        const initialKey = defaultKey && tabs.some((t) => t.key === defaultKey) ? defaultKey : firstTab.key;
        if (!initialKey) {
          return;
        }
        this.activeTabKey.set(initialKey as string);
      }
    });
  }

  ngOnInit(): void {
    this.initializeTabFormGroups();
  }

  ngAfterContentInit(): void {
    // Wait for content projection to complete
    Promise.resolve().then(() => {
      this.registerChildControls();
      this.subscribeToFormChanges();
    });

    runInInjectionContext(this.injector, () => {
      effect(() => {
        const controls = this.childControls();
        if (controls.length > 0) {
          this.registerChildControls();
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  // --- PUBLIC METHODS ---

  /**
   * Get the ID for a specific tab button (for ARIA linkage).
   *
   * @internal
   */
  protected getTabId(key: string): string {
    return `${this.uniqueId}-tab-${key}`;
  }

  /**
   * Get the ID for a specific tab panel (for ARIA linkage).
   *
   * @internal
   */
  protected getPanelId(key: string): string {
    return `${this.uniqueId}-panel-${key}`;
  }

  /**
   * Handle tab click event.
   *
   * @internal
   */
  protected handleTabClick(tab: HxTab): void {
    if (tab.disabled) {
      return;
    }

    const previousKey = this.activeTabKey();
    if (previousKey === tab.key) {
      return; // Already active
    }

    this.activeTabKey.set(tab.key);

    Promise.resolve().then(() => {
      this.validationStatusTrigger.update((v) => v + 1);
    });

    this.tabChange.emit({
      previousKey,
      currentKey: tab.key,
      currentTab: tab,
    });
  }

  /**
   * Handle keyboard navigation on tabs.
   *
   * @internal
   */
  protected handleTabKeydown(event: KeyboardEvent, tab: HxTab): void {
    const tabs = this.enhancedTabs() ?? [];
    if (tabs.length === 0) {
      return;
    }

    const currentIndex = tabs.findIndex((t) => t.key === tab.key);

    let nextIndex: number | null = null;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        break;

      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        break;

      case 'Home':
        event.preventDefault();
        nextIndex = 0;
        break;

      case 'End':
        event.preventDefault();
        nextIndex = tabs.length - 1;
        break;

      case 'Enter':
      case ' ':
        event.preventDefault();
        this.handleTabClick(tab);
        return;
    }

    if (nextIndex === null || nextIndex < 0 || nextIndex >= tabs.length) {
      return;
    }

    const nextTab = tabs[nextIndex] as HxTab | undefined;
    if (!nextTab || nextTab.disabled) {
      return;
    }

    const resolvedNextTab: HxTab = nextTab as HxTab;
    this.handleTabClick(resolvedNextTab);

    setTimeout(() => {
      const tabButton = document.querySelector(`#${this.getTabId(resolvedNextTab.key)}`) as HTMLElement;
      if (tabButton) {
        tabButton.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }, 0);
  }

  /**
   * Check if a tab is currently active.
   *
   * @internal
   */
  protected isTabActive(key: string): boolean {
    return this.activeTabKey() === key;
  }

  /**
   * Get the active panel's DOM element container.
   *
   * @internal
   */
  private getActivePanelElement(): HTMLElement | null {
    const key = this.activeTabKey();
    if (!key) {
      return null;
    }
    return this.hostElementRef.nativeElement.querySelector(`[data-tab-panel-key="${key}"]`);
  }

  /**
   * Get the button element for a tab key.
   *
   * @internal
   */
  private getTabButton(key: string): HTMLButtonElement | null {
    const id = this.getTabId(key);
    return this.hostElementRef.nativeElement.querySelector(`[id="${id}"]`);
  }

  /**
   * Get all currently non-disabled tabs.
   *
   * @internal
   */
  private getEnabledTabs(): HxTab[] {
    return (this.enhancedTabs() ?? []).filter((tab) => !tab.disabled);
  }

  /**
   * Handles Tab / Shift+Tab keyboard navigation across tabs and their panel contents.
   *
   * Forward Tab flow:
   * Tab 1 Button -> Tab 1 Panel Controls -> Tab 2 Button -> Tab 2 Panel Controls -> ... -> next subsection
   *
   * Reverse Shift+Tab flow:
   * Tab 2 First Control -> Tab 2 Button -> Tab 1 Last Control -> ... -> Tab 1 Button -> previous subsection
   *
   * @internal
   */
  protected handleHostKeydown(event: KeyboardEvent): void {
    if (!isPlainTab(event) || event.defaultPrevented) {
      return;
    }

    const active = getActiveElement();
    if (!active || !this.hostElementRef.nativeElement.contains(active)) {
      return;
    }

    const isTabButton = !!active.closest('.hx-tabbed-view__tab');
    const activePanel = this.getActivePanelElement();
    const isInsideActivePanel = !!activePanel && (active === activePanel || activePanel.contains(active));

    if (!isTabButton && !isInsideActivePanel) {
      return;
    }

    const enabledTabs = this.getEnabledTabs();
    if (enabledTabs.length === 0) {
      return;
    }

    let currentTabKey = this.activeTabKey() ?? enabledTabs[0]?.key;
    if (isTabButton) {
      const buttonElement = active.closest<HTMLElement>('.hx-tabbed-view__tab');
      if (buttonElement?.id) {
        const matchingTab = enabledTabs.find((t) => this.getTabId(t.key) === buttonElement.id);
        if (matchingTab) {
          currentTabKey = matchingTab.key;
          if (this.activeTabKey() !== matchingTab.key) {
            this.handleTabClick(matchingTab);
            this.cdr.detectChanges();
          }
        }
      }
    }

    const currentTabIndex = enabledTabs.findIndex((t) => t.key === currentTabKey);

    if (!event.shiftKey) {
      // FORWARD TAB
      if (isTabButton) {
        // From tab button, move into the active panel's first control
        const panel = this.getActivePanelElement();
        const stops = panel ? getTabStops(panel, 'dom', false, false).filter((s) => s !== panel) : [];

        if (stops.length > 0) {
          event.preventDefault();
          event.stopPropagation();
          const firstStop = stops[0]!;
          focusControl(firstStop);
          if (typeof firstStop.scrollIntoView === 'function') {
            firstStop.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
          }
        } else {
          // Panel has no editable child controls: advance to next tab if one exists
          if (currentTabIndex >= 0 && currentTabIndex < enabledTabs.length - 1) {
            const nextTab = enabledTabs[currentTabIndex + 1]!;
            event.preventDefault();
            event.stopPropagation();
            this.handleTabClick(nextTab);
            this.cdr.detectChanges();
            const nextBtn = this.getTabButton(nextTab.key);
            if (nextBtn) {
              nextBtn.focus();
              if (typeof nextBtn.scrollIntoView === 'function') {
                nextBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
              }
            }
          }
          // If this was the last tab and empty, do not preventDefault: let event bubble out to next subsection
        }
      } else if (isInsideActivePanel) {
        const panel = activePanel!;
        const stops = getTabStops(panel, 'dom', false, false).filter((s) => s !== panel);

        let isAtLastStop = false;
        if (stops.length === 0) {
          isAtLastStop = true;
        } else {
          const lastStop = stops[stops.length - 1]!;
          const stopIndex = indexOfTabStop(panel, stops, active);
          isAtLastStop = stopIndex === stops.length - 1 || lastStop === active || lastStop.contains(active);
        }

        if (isAtLastStop) {
          // If there is a next tab, switch to it and focus its tab button
          if (currentTabIndex >= 0 && currentTabIndex < enabledTabs.length - 1) {
            const nextTab = enabledTabs[currentTabIndex + 1]!;
            event.preventDefault();
            event.stopPropagation();
            this.handleTabClick(nextTab);
            this.cdr.detectChanges();
            const nextBtn = this.getTabButton(nextTab.key);
            if (nextBtn) {
              nextBtn.focus();
              if (typeof nextBtn.scrollIntoView === 'function') {
                nextBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
              }
            }
          }
          // If this is the last tab, let event bubble out so HxTabNavigation leaves the tab control
        }
      }
    } else {
      // BACKWARD TAB (Shift + Tab)
      if (isTabButton) {
        // If there is a previous tab, switch to it and focus its last control (or button if empty)
        if (currentTabIndex > 0) {
          const prevTab = enabledTabs[currentTabIndex - 1]!;
          event.preventDefault();
          event.stopPropagation();
          this.handleTabClick(prevTab);
          this.cdr.detectChanges();
          const prevPanel = this.getActivePanelElement();
          const prevStops = prevPanel ? getTabStops(prevPanel, 'dom', false, true).filter((s) => s !== prevPanel) : [];

          if (prevStops.length > 0) {
            const lastStop = prevStops[prevStops.length - 1]!;
            focusControl(lastStop);
            if (typeof lastStop.scrollIntoView === 'function') {
              lastStop.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
            }
          } else {
            const prevBtn = this.getTabButton(prevTab.key);
            if (prevBtn) {
              prevBtn.focus();
              if (typeof prevBtn.scrollIntoView === 'function') {
                prevBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
              }
            }
          }
        }
        // If on the first tab button, do not preventDefault: let event bubble out to previous subsection control
      } else if (isInsideActivePanel) {
        const panel = activePanel!;
        const stops = getTabStops(panel, 'dom', false, true).filter((s) => s !== panel);

        let isAtFirstStop = false;
        if (stops.length === 0) {
          isAtFirstStop = true;
        } else {
          const firstStop = stops[0]!;
          const stopIndex = indexOfTabStop(panel, stops, active);
          isAtFirstStop = stopIndex <= 0 || firstStop === active || firstStop.contains(active);
        }

        if (isAtFirstStop) {
          // Move focus back to the current tab button
          event.preventDefault();
          event.stopPropagation();
          const tabBtn = this.getTabButton(currentTabKey);
          if (tabBtn) {
            tabBtn.focus();
            if (typeof tabBtn.scrollIntoView === 'function') {
              tabBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
            }
          }
        }
      }
    }
  }

  // --- PUBLIC API FOR EXTERNAL CONTROL REGISTRATION ---

  /**
   * Register an external control with a specific tab's FormGroup. Useful when controls are dynamically rendered and not
   * detected by contentChildren.
   *
   * @example
   *   ```typescript
   *   // In a parent component using HxTabbedView
   *   @ViewChild(HxTabbedView) tabbedView!: HxTabbedView;
   *
   *   ngAfterViewInit() {
   *     this.tabbedView.registerExternalControl('tab-1', 'firstName', this.firstNameControl);
   *   }
   *   ```;
   *
   * @param tabKey - The key of the tab to register the control with
   * @param controlId - Unique identifier for the control
   * @param formControl - The AbstractControl instance to register
   */
  public registerExternalControl(tabKey: string, controlId: string, formControl: AbstractControl): void {
    const formGroup = this.tabFormGroups.get(tabKey);
    if (!formGroup) {
      console.warn(`[HxTabbedView] Cannot register control '${controlId}': tab '${tabKey}' not found`);
      return;
    }

    // Only add if not already registered
    if (!formGroup.contains(controlId)) {
      formGroup.addControl(controlId, formControl);

      // Subscribe to this control's status changes
      const statusSub = formControl.statusChanges.subscribe(() => {
        this.tabValidationChange.emit({
          tabKey,
          isValid: formGroup.valid,
          errors: formGroup.errors,
        });

        Promise.resolve().then(() => {
          this.validationStatusTrigger.update((v) => v + 1);
        });

        this.emitOverallValidation();
      });

      this.subscriptions.push(statusSub);

      // Trigger initial validation update
      this.validationStatusTrigger.update((v) => v + 1);
    }
  }

  /**
   * Bulk register multiple external controls across tabs. This is a convenience method to register many controls at
   * once, reducing boilerplate in consuming components.
   *
   * @example
   *   ```typescript
   *   @ViewChild(HxTabbedView) tabbedView!: HxTabbedView;
   *
   *   ngAfterViewInit() {
   *     this.tabbedView.registerControlsFromItems([
   *       { key: 'tab-1', items: [{ id: 'firstName', formControl: this.firstNameControl }] },
   *       { key: 'tab-2', items: [{ id: 'address', formControl: this.addressControl }] }
   *     ]);
   *   }
   *   ```;
   *
   * @param tabs - Array of tab configurations with their items to register
   */
  public registerControlsFromItems(
    tabs: Array<{ key: string; items: Array<{ id?: string; formControl?: AbstractControl }> }>
  ): void {
    tabs.forEach((tab) => {
      tab.items.forEach((item) => {
        if (item.id && item.formControl) {
          this.registerExternalControl(tab.key, item.id, item.formControl);
        }
      });
    });

    // Trigger validation status refresh after all registrations
    this.refreshValidation();
  }

  /**
   * Unregister an external control from a tab's FormGroup.
   *
   * @param tabKey - The key of the tab to unregister the control from
   * @param controlId - Unique identifier for the control
   */
  public unregisterExternalControl(tabKey: string, controlId: string): void {
    const formGroup = this.tabFormGroups.get(tabKey);
    if (formGroup && formGroup.contains(controlId)) {
      formGroup.removeControl(controlId);
      this.validationStatusTrigger.update((v) => v + 1);
    }
  }

  /**
   * Force a refresh of validation status for all tabs. Useful after batch control registration or when validation state
   * changes externally.
   */
  public refreshValidation(): void {
    this.validationStatusTrigger.update((v) => v + 1);
    this.emitOverallValidation();
  }

  // --- CONTROL VALUE ACCESSOR IMPLEMENTATION ---

  writeValue(value: Record<string, any> | null): void {
    if (value) {
      this.currentValue = value;
      Object.keys(value).forEach((tabKey) => {
        const formGroup = this.tabFormGroups.get(tabKey);
        if (formGroup && value[tabKey]) {
          formGroup.patchValue(value[tabKey], { emitEvent: false });
        }
      });
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.tabFormGroups.forEach((formGroup) => {
      isDisabled ? formGroup.disable() : formGroup.enable();
    });
  }

  // --- FORM MANAGEMENT HELPERS ---

  /** Initialize FormGroup for each tab */
  protected initializeTabFormGroups(): void {
    const tabs = this.tabs();

    if (!tabs) return;

    tabs.forEach((tab) => {
      if (!this.tabFormGroups.has(tab.key)) {
        const formGroup = new FormGroup({});
        this.tabFormGroups.set(tab.key, formGroup);
      }
    });
  }

  /** Find which tab panel a control belongs to */
  protected findTabForControl(element: HTMLElement | null): string | null {
    if (!element) return null;

    let current: HTMLElement | null = element;

    while (current) {
      const tabKey = current.getAttribute('data-tab-panel-key');
      if (tabKey) return tabKey;
      current = current.parentElement;
    }

    return null;
  }

  /**
   * Get the native element from an NgControl (FormControl directive). Uses the injector-based ElementRef lookup for
   * reactive forms. For wrapper components (like HxInput), searches for child element with id attribute.
   */
  protected getControlElement(ngControl: NgControl): HTMLElement | null {
    let element: HTMLElement | null = null;
    // CASE 1: Try NgControl's own injector (standard reactive forms)
    try {
      const injector = (ngControl as any).injector as Injector;
      if (injector) {
        const elementRef = injector.get(ElementRef, null);
        if (elementRef?.nativeElement) {
          element = elementRef.nativeElement;
        }
      }
    } catch (e) {
      // Fallback to alternative element lookups
    }

    // CASE 2: Try direct element reference (template-driven forms)
    if (!element) {
      const directRef = (ngControl as any)._elementRef?.nativeElement;
      if (directRef) {
        element = directRef;
      }
    }

    // CASE 3: Try to get element from valueAccessor (for wrapper components like HxInput)
    if (!element) {
      const valueAccessor = ngControl.valueAccessor;

      if (valueAccessor) {
        try {
          // Angular components store their injector, which we can use to get ElementRef
          const valueAccessorInjector = (valueAccessor as any)._injector;
          if (valueAccessorInjector) {
            const elementRef = valueAccessorInjector.get(ElementRef, null);
            if (elementRef?.nativeElement) {
              element = elementRef.nativeElement;
            }
          }
        } catch (error) {
          console.error('[HxTabbedView] Error getting element from valueAccessor:', error);
        }
      }
    }

    if (!element) {
      return null;
    }

    // If element has id, use it directly
    if (element.id) return element;

    // For wrapper components, search for child element with id attribute
    const childWithId = element.querySelector('[id]') as HTMLElement;
    if (childWithId?.id) {
      return childWithId;
    }

    return null;
  }

  /**
   * Register all child controls into their respective tab FormGroups. Since all tabs are now rendered (just hidden with
   * CSS), we can register all controls at once. Requires FormControl with id attribute on each control element.
   */
  protected registerChildControls(): void {
    const controls = this.childControls();
    if (controls.length === 0) return;

    // Clear existing controls
    this.tabFormGroups.forEach((formGroup) => {
      Object.keys(formGroup.controls).forEach((key) => formGroup.removeControl(key));
    });

    // Register each control to its tab's FormGroup
    controls.forEach((ngControl) => {
      const controlElement = this.getControlElement(ngControl);
      if (!controlElement?.id) {
        // Skip controls without id - they cannot be tracked
        return;
      }

      const controlKey = controlElement.id;
      const tabKey = this.findTabForControl(controlElement);
      if (!tabKey) return;

      const formGroup = this.tabFormGroups.get(tabKey);
      const formControl = ngControl.control;

      if (formGroup && formControl) {
        formGroup.addControl(controlKey, formControl);
      }
    });

    // Trigger validation status refresh
    this.validationStatusTrigger.update((v) => v + 1);
  }

  /** Subscribe to FormGroup changes and emit aggregated value */
  protected subscribeToFormChanges(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];

    this.tabFormGroups.forEach((formGroup, tabKey) => {
      const valueSub = formGroup.valueChanges.subscribe((tabValue) => {
        const hasControls = Object.keys(formGroup.controls).length > 0;
        if (!hasControls) return;

        const existingTabValue = this.currentValue[tabKey] || {};
        this.currentValue = {
          ...this.currentValue,
          [tabKey]: { ...existingTabValue, ...tabValue },
        };

        this.onChange(this.currentValue);

        Promise.resolve().then(() => {
          this.validationStatusTrigger.update((v) => v + 1);
        });
      });

      const statusSub = formGroup.statusChanges.subscribe(() => {
        this.tabValidationChange.emit({
          tabKey,
          isValid: formGroup.valid,
          errors: formGroup.errors,
        });

        Promise.resolve().then(() => {
          this.validationStatusTrigger.update((v) => v + 1);
        });

        this.emitOverallValidation();
      });

      this.subscriptions.push(valueSub, statusSub);
    });
  }

  /** Emit overall validation status */
  protected emitOverallValidation(): void {
    const invalidTabs: string[] = [];
    let isValid = true;

    const tabs = this.tabs();
    if (!tabs) return;

    tabs.forEach((tab) => {
      const formGroup = this.tabFormGroups.get(tab.key);

      // Report actual FormGroup validity regardless of interaction state
      // The parent container decides whether to display errors based on touched state
      if (formGroup && !formGroup.valid) {
        isValid = false;
        invalidTabs.push(tab.key);
      }
    });

    this.validationChange.emit({ isValid, invalidTabs });
  }

  /**
   * Compute validation status for a tab based on its FormGroup. Always uses 'all-valid' strategy: tab is complete when
   * all required controls are valid.
   *
   * @param tabKey - The key of the tab to compute status for
   * @returns Tab status: 'valid', 'invalid', or 'incomplete'
   */
  protected computeTabStatus(tabKey: string): 'valid' | 'invalid' | 'incomplete' {
    const formGroup = this.tabFormGroups.get(tabKey);

    // No form group or no controls => incomplete
    if (!formGroup) return 'incomplete';

    const controls = Object.values(formGroup.controls);
    if (controls.length === 0) return 'incomplete';

    // Check if user has interacted with any controls
    const hasInteractedControls = controls.some((c) => c.touched || c.dirty);

    // No interaction yet
    if (!hasInteractedControls) return 'incomplete';

    // Check for validation errors
    const hasInvalidControls = controls.some((c) => c.status === 'INVALID');
    if (hasInvalidControls) return 'invalid';

    // All controls are valid
    return 'valid';
  }
}
